import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react'

import { LiquiditySource } from '@src/enums'

export interface ExchangeState {
    isGasFeeExpanded: boolean
    liquiditySource: LiquiditySource
    isBuyOutputLoading: boolean
    isQuoteInvalid: boolean
    machFee: number
    isReviewSwapModalOpen: boolean
    isInsufficientBalance: boolean
    isOrderRejected: boolean
    isQuoteEnabled: boolean
    isQuoteExpired: boolean
    isManuallyRefreshing: boolean
    isCustomWallet: boolean
    hasInsufficientGas: boolean
    gasAmountNeededString: string
    sellAmountString: string
    isPriceFlipped: boolean
    isToggleInProgress: boolean
    chainId: number
    sellAllowance?: bigint
    transactionCap: string | null
    isTransactionCapped: boolean
    transactionCapRemainingAllowance: string | null
    transactionCapNextUnlock: { hours: number; minutes: number; seconds: number } | null
}

interface ExchangeStateContextType extends ExchangeState {
    setIsGasFeeExpanded: React.Dispatch<React.SetStateAction<boolean>>
    setLiquiditySource: React.Dispatch<React.SetStateAction<LiquiditySource>>
    setIsBuyOutputLoading: React.Dispatch<React.SetStateAction<boolean>>
    setIsQuoteInvalid: React.Dispatch<React.SetStateAction<boolean>>
    setMachFee: React.Dispatch<React.SetStateAction<number>>
    setIsReviewSwapModalOpen: React.Dispatch<React.SetStateAction<boolean>>
    setIsInsufficientBalance: React.Dispatch<React.SetStateAction<boolean>>
    setIsOrderRejected: React.Dispatch<React.SetStateAction<boolean>>
    setIsQuoteEnabled: React.Dispatch<React.SetStateAction<boolean>>
    setIsQuoteExpired: React.Dispatch<React.SetStateAction<boolean>>
    setIsManuallyRefreshing: React.Dispatch<React.SetStateAction<boolean>>
    setIsCustomWallet: React.Dispatch<React.SetStateAction<boolean>>
    setHasInsufficientGas: React.Dispatch<React.SetStateAction<boolean>>
    setGasAmountNeededString: React.Dispatch<React.SetStateAction<string>>
    setSellAmountString: React.Dispatch<React.SetStateAction<string>>
    setIsPriceFlipped: React.Dispatch<React.SetStateAction<boolean>>
    setIsToggleInProgress: React.Dispatch<React.SetStateAction<boolean>>
    setChainId: React.Dispatch<React.SetStateAction<number>>
    setSellAllowance: React.Dispatch<React.SetStateAction<bigint | undefined>>
    setTransactionCap: React.Dispatch<React.SetStateAction<string | null>>
    setIsTransactionCapped: React.Dispatch<React.SetStateAction<boolean>>
    setTransactionCapRemainingAllowance: React.Dispatch<React.SetStateAction<string | null>>
    setTransactionCapNextUnlock: React.Dispatch<
        React.SetStateAction<{ hours: number; minutes: number; seconds: number } | null>
    >
}

const ExchangeStateContext = createContext<ExchangeStateContextType | undefined>(undefined)

interface ExchangeStateProviderProps {
    children: ReactNode
}

export const ExchangeStateProvider: React.FC<ExchangeStateProviderProps> = ({ children }) => {
    const [isGasFeeExpanded, setIsGasFeeExpanded] = useState<boolean>(false)
    const [liquiditySource, setLiquiditySource] = useState<LiquiditySource>(LiquiditySource.unavailable)
    const [isBuyOutputLoading, setIsBuyOutputLoading] = useState<boolean>(false)
    const [isQuoteInvalid, setIsQuoteInvalid] = useState<boolean>(false)
    const [machFee, setMachFee] = useState<number>(0)
    const [isReviewSwapModalOpen, setIsReviewSwapModalOpen] = useState<boolean>(false)
    const [isInsufficientBalance, setIsInsufficientBalance] = useState<boolean>(false)
    const [isOrderRejected, setIsOrderRejected] = useState<boolean>(false)
    const [isQuoteEnabled, setIsQuoteEnabled] = useState<boolean>(false)
    const [isQuoteExpired, setIsQuoteExpired] = useState<boolean>(false)
    const [isManuallyRefreshing, setIsManuallyRefreshing] = useState<boolean>(false)
    const [isCustomWallet, setIsCustomWallet] = useState<boolean>(false)
    const [hasInsufficientGas, setHasInsufficientGas] = useState<boolean>(false)
    const [gasAmountNeededString, setGasAmountNeededString] = useState<string>('')
    const [sellAmountString, setSellAmountString] = useState<string>('')
    const [isPriceFlipped, setIsPriceFlipped] = useState<boolean>(false)
    const [isToggleInProgress, setIsToggleInProgress] = useState<boolean>(false)
    const [chainId, setChainId] = useState<number>(0)
    const [sellAllowance, setSellAllowance] = useState<bigint | undefined>(undefined)
    const [isTransactionCapped, setIsTransactionCapped] = useState<boolean>(false)
    const [transactionCap, setTransactionCap] = useState<string | null>(null)
    const [transactionCapRemainingAllowance, setTransactionCapRemainingAllowance] = useState<string | null>(null)
    const [transactionCapNextUnlock, setTransactionCapNextUnlock] = useState<{
        hours: number
        minutes: number
        seconds: number
    } | null>(null)

    const contextValue: ExchangeStateContextType = {
        isGasFeeExpanded,
        setIsGasFeeExpanded,
        liquiditySource,
        setLiquiditySource,
        isBuyOutputLoading,
        setIsBuyOutputLoading,
        isQuoteInvalid,
        setIsQuoteInvalid,
        machFee,
        setMachFee,
        isReviewSwapModalOpen,
        setIsReviewSwapModalOpen,
        isInsufficientBalance,
        setIsInsufficientBalance,
        isOrderRejected,
        setIsOrderRejected,
        isQuoteEnabled,
        setIsQuoteEnabled,
        isQuoteExpired,
        setIsQuoteExpired,
        isManuallyRefreshing,
        setIsManuallyRefreshing,
        isCustomWallet,
        setIsCustomWallet,
        hasInsufficientGas,
        setHasInsufficientGas,
        gasAmountNeededString,
        setGasAmountNeededString,
        sellAmountString,
        setSellAmountString,
        isPriceFlipped,
        setIsPriceFlipped,
        isToggleInProgress,
        setIsToggleInProgress,
        chainId,
        setChainId,
        sellAllowance,
        setSellAllowance,
        transactionCap,
        setTransactionCap,
        isTransactionCapped,
        setIsTransactionCapped,
        transactionCapRemainingAllowance,
        setTransactionCapRemainingAllowance,
        transactionCapNextUnlock,
        setTransactionCapNextUnlock,
    }

    return <ExchangeStateContext.Provider value={contextValue}>{children}</ExchangeStateContext.Provider>
}

export function useExchangeState() {
    const context = useContext(ExchangeStateContext)
    if (!context) {
        throw new Error('useExchangeState must be used within an ExchangeStateProvider')
    }
    return context
}
